import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { UploadIcon, XIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, ErrorText, LoadingIndicator, Row, Text } from "@/components";
import { FormEnumSelect } from "@/components/form/FormEnumSelect";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";

import { useCreateSourcingResumeBatch } from "@/hooks/mutations/useCreateSourcingResumeBatch";
import { EXTERNAL_RESUME_SOURCE_LABELS } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { ExternalResumeSource } from "@/types";

const ACCEPTED_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const ACCEPT_ATTRIBUTE = ".pdf,.doc,.docx";

const schema = z.object({
  source: z.nativeEnum(ExternalResumeSource, {
    required_error: "Pick where these resumes came from.",
  }),
});

export const SourcingResumeBatchDialog = ({
  onOpenChange,
  onSuccess,
  open,
}: {
  onOpenChange: (open: boolean) => void;
  onSuccess: (fileCount: number) => void;
  open: boolean;
}) => {
  /* React Hooks */
  const [resumes, setResumes] = useState<File[]>([]);
  const [rejectedNames, setRejectedNames] = useState<string[]>([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  });

  const {
    mutate: createBatch,
    isError,
    isPending,
    reset: resetMutation,
  } = useCreateSourcingResumeBatch();

  /* Handlers */
  // Also clears the mutation, so a failed attempt's error is not still on
  // screen the next time the dialog opens.
  const reset = () => {
    form.reset();
    resetMutation();
    setResumes([]);
    setRejectedNames([]);
    setIsDraggingOver(false);
  };

  // Dedupe by name and size: dropping the same folder twice is an easy mistake
  // to make, and each duplicate would otherwise be rated and texted twice.
  const addFiles = (incoming: File[]) => {
    const accepted = incoming.filter((file) =>
      ACCEPTED_MIME_TYPES.includes(file.type),
    );
    const rejected = incoming.filter(
      (file) => !ACCEPTED_MIME_TYPES.includes(file.type),
    );

    setResumes((current) => {
      const seen = new Set(
        current.map((file) => `${file.name}:${file.size.toString()}`),
      );
      const additions = accepted.filter(
        (file) => !seen.has(`${file.name}:${file.size.toString()}`),
      );
      return [...current, ...additions];
    });
    setRejectedNames(rejected.map((file) => file.name));
  };

  const handleSubmit = (data: z.infer<typeof schema>) => {
    createBatch(
      {
        createSourcingResumeBatchRequest: { resumes, source: data.source },
      },
      {
        onSuccess: (batch) => {
          onSuccess(batch.fileCount);
          onOpenChange(false);
          reset();
        },
      },
    );
  };

  /* Render */
  return (
    <Dialog
      onOpenChange={(isOpen) => {
        if (!isOpen) reset();
        onOpenChange(isOpen);
      }}
      open={open}
    >
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Add sourced resumes</DialogTitle>
          <DialogDescription>
            Drop the resumes you unlocked elsewhere. Each new person gets a
            profile, is rated against this job, and is handed to Sally for
            outreach. Anyone already on Laborup is skipped. This runs in the
            background and emails a summary when it finishes.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            className="flex flex-col gap-5"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormEnumSelect
              fieldName="source"
              form={form}
              label="Where did these come from?"
              options={Object.values(ExternalResumeSource).map((source) => ({
                value: source,
                label: EXTERNAL_RESUME_SOURCE_LABELS[source],
              }))}
              placeholder="Pick a source"
            />

            <Column className="gap-2">
              <Text variant="secondary">Resumes</Text>
              <label
                className={cn(
                  "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed border-slate-300 px-4 py-8 text-center transition-colors",
                  isDraggingOver && "border-slate-500 bg-slate-50",
                )}
                onDragLeave={() => {
                  setIsDraggingOver(false);
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  setIsDraggingOver(true);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  setIsDraggingOver(false);
                  addFiles(Array.from(event.dataTransfer.files));
                }}
              >
                <UploadIcon className="size-5 text-slate-400" />
                <Text variant="muted">
                  Drop PDF or Word resumes here, or click to choose them
                </Text>
                <input
                  accept={ACCEPT_ATTRIBUTE}
                  className="hidden"
                  multiple
                  onChange={(event) => {
                    addFiles(Array.from(event.target.files ?? []));
                    // Let the same file be picked again after a removal.
                    event.target.value = "";
                  }}
                  type="file"
                />
              </label>

              {rejectedNames.length > 0 && (
                <ErrorText
                  errorMessage={`Skipped ${rejectedNames.length.toString()} file(s) that aren't PDF or Word: ${rejectedNames.join(", ")}`}
                />
              )}

              {resumes.length > 0 && (
                <Column className="gap-1">
                  <Row className="items-center justify-between">
                    <Text variant="muted">
                      {resumes.length.toString()} resume
                      {resumes.length === 1 ? "" : "s"} ready
                    </Text>
                    <Button
                      onClick={() => {
                        setResumes([]);
                      }}
                      size="sm"
                      type="button"
                      variant="ghost"
                    >
                      Clear all
                    </Button>
                  </Row>
                  <Column className="max-h-48 gap-1 overflow-y-auto rounded-md border border-slate-200 p-2">
                    {resumes.map((resume) => (
                      <Row
                        className="items-center justify-between gap-2"
                        key={`${resume.name}:${resume.size.toString()}`}
                      >
                        <Text className="truncate" variant="muted">
                          {resume.name}
                        </Text>
                        <Button
                          onClick={() => {
                            setResumes((current) =>
                              current.filter((file) => file !== resume),
                            );
                          }}
                          size="icon"
                          type="button"
                          variant="ghost"
                        >
                          <XIcon className="size-4" />
                        </Button>
                      </Row>
                    ))}
                  </Column>
                </Column>
              )}
            </Column>

            {isError && (
              <ErrorText errorMessage="Could not start the batch. Nothing was queued." />
            )}

            <DialogFooter>
              <Button
                disabled={isPending}
                onClick={() => {
                  reset();
                  onOpenChange(false);
                }}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                disabled={resumes.length === 0 || isPending}
                type="submit"
              >
                {isPending ? (
                  <LoadingIndicator gearColor="white" variant="small" />
                ) : (
                  <span>Start sourcing</span>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
